import {CurrencyAmount} from './payroll-currency.model';
import {
  DEAL_PAYROLL_STATUS,
  DealPayrollBreakdown,
  DEAL_PAYROLL_FORMULA_VERSION,
  isDealPayrollIdentityMatch,
  isDealPayrollPeriodDiagnostic
} from './deal-payroll.model';
import {
  COMPLETE_LABEL_KEY,
  DISABLED_LABEL_KEY,
  DISABLED_PREVIEW_LABEL_KEY,
  FIRST_AMOUNT_INDEX,
  INCOMPLETE_LABEL_KEY,
  MODIFIER_SOURCE_KEY,
  REMOTE_MODIFIER_SOURCE
} from './deal-payroll-report-presentation.constants';
import {
  DealPayrollSalaryPresentation,
  DealPayrollSalaryPresentationSource,
  DealPayrollSalarySummary
} from './deal-payroll-report-presentation.types';

export {
  DealPayrollSalaryPresentation,
  DealPayrollSalaryPresentationSource,
  DealPayrollSalarySummary
} from './deal-payroll-report-presentation.types';

export function hasTrustedDealPayrollModifiers(report: Object): boolean {
  return !report || report[MODIFIER_SOURCE_KEY] !== REMOTE_MODIFIER_SOURCE;
}

export function markDealPayrollModifiersRemote(report: Object): void {
  Object.defineProperty(report, MODIFIER_SOURCE_KEY, {
    configurable: true,
    enumerable: false,
    value: REMOTE_MODIFIER_SOURCE,
    writable: true
  });
}

export function dealPayrollSalaryPresentation(
  report: DealPayrollSalaryPresentationSource
): DealPayrollSalaryPresentation {
  const breakdown = report && report.dealPayrollBreakdown;
  const official = report && report.summaryRecord && report.summaryRecord.salaryAmounts || [];
  if (!breakdown) {
    return {amounts: official, preview: false, labelKey: COMPLETE_LABEL_KEY};
  }
  if (hasNonPayablePreview(breakdown)) {
    return nonPayablePresentation(breakdown);
  }
  return {
    amounts: official,
    preview: false,
    labelKey: dealPayrollLabelKey(breakdown)
  };
}

export function synchronizeDealPayrollSalary(
  report: DealPayrollSalaryPresentationSource,
  breakdown: DealPayrollBreakdown,
  expectedMonth: string
): boolean {
  if (!canSynchronizeDealPayrollSalary(report, breakdown, expectedMonth)) {
    return false;
  }
  report.dealPayrollBreakdown = breakdown;
  if (breakdown.status !== DEAL_PAYROLL_STATUS.COMPLETE) {
    clearOfficialSalary(report.summaryRecord);
    return true;
  }
  const official = breakdown.finalSalaryAmounts;
  if (!official || !official.length) {
    return true;
  }
  synchronizeOfficialSalary(report.summaryRecord, breakdown, official);
  return true;
}

function canSynchronizeDealPayrollSalary(
  report: DealPayrollSalaryPresentationSource,
  breakdown: DealPayrollBreakdown,
  expectedMonth: string
): boolean {
  return !!report && !!report.summaryRecord && !!breakdown
    && breakdown.formulaVersion === DEAL_PAYROLL_FORMULA_VERSION
    && (isDealPayrollIdentityMatch(report.userID, expectedMonth, breakdown)
      || isDealPayrollPeriodDiagnostic(report.userID, breakdown));
}

function nonPayablePresentation(breakdown: DealPayrollBreakdown): DealPayrollSalaryPresentation {
  return {
    amounts: breakdown.finalSalaryAmounts,
    preview: true,
    labelKey: breakdown.status === DEAL_PAYROLL_STATUS.DISABLED
      ? DISABLED_PREVIEW_LABEL_KEY
      : INCOMPLETE_LABEL_KEY
  };
}

function hasNonPayablePreview(breakdown: DealPayrollBreakdown): boolean {
  return breakdown.status !== DEAL_PAYROLL_STATUS.COMPLETE
    && !!breakdown.finalSalaryAmounts
    && !!breakdown.finalSalaryAmounts.length;
}

function clearOfficialSalary(summary: DealPayrollSalarySummary): void {
  summary.salaryAmounts = [];
  summary.orderedSalaryAmounts = [];
  summary.salarySupplements = null;
  summary.salarySupplementsCurrency = null;
  summary.salarySupplementsCurrencyEur = null;
  summary.salarySupplementsCurrencyCny = null;
}

function dealPayrollLabelKey(breakdown: DealPayrollBreakdown): string {
  if (breakdown.status === DEAL_PAYROLL_STATUS.INCOMPLETE) {
    return INCOMPLETE_LABEL_KEY;
  }
  return breakdown.status === DEAL_PAYROLL_STATUS.DISABLED ? DISABLED_LABEL_KEY : COMPLETE_LABEL_KEY;
}

function synchronizeOfficialSalary(
  summary: DealPayrollSalarySummary,
  breakdown: DealPayrollBreakdown,
  official: CurrencyAmount[]
): void {
  summary.salaryAmounts = official.slice();
  if (breakdown.salaryCurrency) {
    summary.primaryCurrency = breakdown.salaryCurrency;
  }
  const primaryCurrency = summary.primaryCurrency;
  const primary = official.filter((amount: CurrencyAmount) =>
    amount.currency === primaryCurrency)[FIRST_AMOUNT_INDEX];
  if (primary) {
    summary.salarySupplements = primary.amount;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/models/deal-payroll-report-presentation.ts